"use client";

import Link from "next/link";
import { useEffect, useState, Suspense } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { shuffleArray } from "@audacia-hq/shared/utils";

import Button from "../components/Button";
import { useExpert } from "../context/ExpertContext";
import ExpertDetailCard from "../components/experts/ExpertDetailCard";
import { Expert, ExpertIdentifier } from "../models/expert";

const PageNotFound = () => {
  const { t } = useTranslation(["horoscope", "common"]);

  const {
    getExperts, expertIdentifiers, expertUpdateFeed, expertsDataLoading,
  } = useExpert();
  const [suggestedUids, setSuggestedUids] = useState<string[]>([]);
  const [suggestedExperts, setSuggestedExperts] = useState<Expert[]>([]);

  const nbSuggestions = 5;

  const findOnlines = (
    list: ExpertIdentifier[],
    amount: number,
    exclude: string[] = [],
  ) => shuffleArray(list.filter(({ expertUid, online, hidden }) => online && !hidden && !exclude.includes(expertUid)))
    .slice(0, amount)
    .map((e) => e.expertUid);

  useEffect(() => {
    setSuggestedUids(findOnlines(Object.values(expertIdentifiers), nbSuggestions));
  }, []);

  useEffect(() => {
    if (suggestedUids.length > 0) {
      getExperts(suggestedUids).then((experts) => setSuggestedExperts(experts));
    }
  }, [suggestedUids]);

  useEffect(() => {
    const sub = expertUpdateFeed.subscribe((expertUids) => {
      if (!expertUids.some((uid) => suggestedUids.includes(uid))) return;
      const onlines = suggestedUids.filter((uid) => expertIdentifiers[uid]?.online && !expertIdentifiers[uid].hidden);
      if (onlines.length < nbSuggestions) {
        setSuggestedUids([
          ...onlines,
          ...findOnlines(Object.values(expertIdentifiers), nbSuggestions - onlines.length, onlines),
        ]);
      }
    });
    return () => sub.unsubscribe();
  }, [expertUpdateFeed, expertIdentifiers]);

  return (
    <div className="flex justify-center items-center flex-col wrapper p-6">
      <div className="container flex flex-col items-center" data-pw="error404">
        <span className="text-blue-600">{t("common:error_404")}</span>
        <span className="text-4xl mt-4 font-bold text-center">
          {t("common:page_not_found")}
        </span>
        <span className="text-gray-500 mt-4 text-sm">
          {t("common:go_to_homepage_or_try_later")}
        </span>
        <Link href="/">
          <Button render="blue" className="mt-6 max-w-[400px]">
            {t("common:go_back_to_homepage")}
          </Button>
        </Link>
        <Suspense>
          <span
            style={{ marginTop: 100 }}
            className="text-gray-900 text-md font-bold"
          >
            {t("common:check_experts_suggestion")}
          </span>

          {(expertsDataLoading) && (
            <div className="w-[50px] flex items-center justify-center mt-4">
              <FontAwesomeIcon
                className="text-5xl text-slate-500"
                icon={faCircleNotch}
                spin
              />
            </div>
          )}

          <div className="mt-6">
            {suggestedExperts && suggestedExperts.length > 0 && (
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {suggestedExperts.map((suggestedExpert, idx) => (
                  <ExpertDetailCard
                    key={`suggested-expert-${suggestedExpert.expertUid}-${idx}`}
                    expert={suggestedExpert}
                    miniature
                    roundedTop
                    expertLink
                    imageOverlayTheme="light"
                  />
                ))}
              </div>
            )}
          </div>
        </Suspense>
      </div>
    </div>
  );
};

export default PageNotFound;
